import { PieController, Chart, ArcElement } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

import { formatNumberAsInteger } from '@/src/utilities/formatters';

Chart.register(PieController, ArcElement, ChartDataLabels);

/** @type {[number, number]} */
const chartWidthScale = [300, 1000];

/**
 * Creates a cost summary pie chart
 * @param {{
 *  chartId: string
 *  imageId: string
 *  dataPoints: ChartDataPoint[]
 *  label: string,
 * }} options
 * @returns {Chart}
 */
export function createChart({ chartId, imageId, dataPoints, label }) {
  const labels = dataPoints.map((v) => v.label);
  const data = dataPoints.map((v) => v.value);
  const backgroundColor = dataPoints.map((v) => v.backgroundColor);
  const images = dataPoints.map((v) => {
    const img = new Image();
    img.src = v.imagePath;
    return img;
  });

  return new Chart(chartId, {
    type: 'pie',
    plugins: [imagePlugin(images)],
    data: {
      labels,
      datasets: [
        {
          normalized: true,
          label,
          data,
          backgroundColor,
          datalabels: {
            anchor: 'center',
            /**
             * Ensures that top left and top right values do not overlap (since the text is horizontal)
             * and that the labels don't cover the icons (however this isn't always possible)
             */
            align: (ctx) => {
              /** @type {{ [key: number]: 'left' | 'right'}} */
              const alignments = ['right', 'left', 'right', 'left'];
              return alignments[ctx.dataIndex];
            },
            /**
             * Ensures the lables don't cover the icons
             */
            offset(ctx) {
              const offsets = [-5, -30, -10, 0];

              return offsets[ctx.dataIndex];
            },
            backgroundColor: '#fff',
            borderColor: '#000',
            padding: 6,
            opacity: 0.9,
            clamp: true,
            color: 'rgba(165, 5, 51, 1)',
            font: ({ chart }) => ({
              size: scaleValue(chartWidthScale, [20, 40], chart.width),
            }),
            textStrokeWidth: 1,
            formatter: (value, _context) => `$${formatNumberAsInteger(value)}`,
          },
        },
      ],
    },
    options: {
      elements: {
        arc: {
          borderAlign: 'center',
          hoverOffset(context, _options) {
            /** @type {Chart} */
            const chart = context.chart;

            return scaleValue(chartWidthScale, [25, 50], chart.width);
          },
        },
      },
      layout: {
        padding(context, _options) {
          /** @type {Chart} */
          const chart = context.chart;

          return scaleValue(chartWidthScale, [25, 75], chart.width);
        },
      },
      interaction: {
        mode: 'nearest',
      },
      animation: {
        animateRotate: true,
        animateScale: true,

        onComplete: function () {
          const image = this.toBase64Image();

          /** @type {HTMLImageElement|null} */
          const imageEl = document.querySelector(`#${imageId}`);

          if (!imageEl) {
            return;
          }

          imageEl.src = image;
        },
      },
    },
  });
}

/** Used to position the step SVGs near the outer edge of the pie segments */
const imagePlugin = (images) => ({
  id: 'Image',

  afterDatasetDraw(chart, { meta: { data } }, _options) {
    /** @type {any[]} */
    const metaData = data;
    const { ctx } = chart;

    ctx.save();

    for (const [index, image] of images.entries()) {
      /** @type {ArcElement} */
      const arc = metaData[index];

      const { x: midX, y: midY } = arc.getCenterPoint();

      const { startAngle, endAngle, innerRadius, outerRadius } = arc.getProps(
        ['startAngle', 'endAngle', 'innerRadius', 'outerRadius'],
        true,
      );

      const middleAngle = startAngle + (endAngle - startAngle) / 2;
      const midRadius = innerRadius + (outerRadius - innerRadius) / 4;

      const imageWidth = scaleValue(chartWidthScale, [50, 100], chart.width);

      // Compute image location
      const posX = midRadius * Math.cos(middleAngle) + midX - imageWidth / 2;
      const posY = midRadius * Math.sin(middleAngle) + midY - imageWidth / 2;

      ctx.drawImage(image, posX, posY, imageWidth, imageWidth);
    }

    ctx.restore();
  },
});

/**
 * Scales a value from its location in one range to the same relative
 * value in another range
 * @param {[number, number]} rangeFrom
 * @param {[number, number]} rangeTo
 * @param {number} fromValue
 */
function scaleValue([fromMin, fromMax], [toMin, toMax], fromValue) {
  fromValue = fromValue <= 0 ? fromMin : fromValue;

  return (
    ((fromValue - fromMin) / (fromMax - fromMin)) * (toMax - toMin) + toMin
  );
}

/**
 * @typedef {import('chart.js').FontSpec} FontSpec
 * @typedef {import('chart.js').Scriptable<FontSpec, any>} ScriptableFontSpec
 * @typedef {import('chart.js').Plugin} Plugin
 * @typedef {{ label: string, value: number, imagePath: string, backgroundColor: string }} ChartDataPoint
 */
